/* eslint-disable react-native/no-inline-styles */
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { View, StyleSheet, ScrollView, TouchableOpacity } from 'react-native';
import { Ionicons } from '@expo/vector-icons';

import { Loader } from '@components/screen/Layout';
import SociollaText from '@components/sociolla/SociollaText';
import Image from '@components/global/Image';
import SearchInputMobile from '@components/global/SearchInputMobile';
import CatalogTab from '@/screens/catalog/components/CatalogTab';

import Colors from '@global/colors';
import { fullWidth, fullHeight } from '@global/screens';
import { showPopup } from '@/store/modal';
import { paginationAdded } from '@/store/params';

import PropTypes from 'prop-types';

const propTypes = {
  style: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  name: PropTypes.string,
  placeholder: PropTypes.string,
  showFilterMobile: PropTypes.bool,
  onExportPress: PropTypes.func,
  scrollRef: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  showSearch: PropTypes.bool,
  loading: PropTypes.bool,
  activeTab: PropTypes.string,
  onChangeTab: PropTypes.func,
  data: PropTypes.array,
  hasFilter: PropTypes.bool,
  headerSticky: PropTypes.oneOfType([PropTypes.bool, PropTypes.node]),
  headerComponent: PropTypes.oneOfType([PropTypes.bool, PropTypes.node]),
  onSearch: PropTypes.func,
  renderItem: PropTypes.func,
  withLoadMore: PropTypes.bool,
  withFetch: PropTypes.bool,
  loadingPagination: PropTypes.bool,
  skip: PropTypes.number,
  limit: PropTypes.number,
  totalData: PropTypes.number,
  onLoadMore: PropTypes.func,
  mainBanner: PropTypes.oneOfType([PropTypes.node, PropTypes.bool]),
};

const defaultProps = {
  name: '',
  showFilterMobile: false,
  onExportPress: null,
  placeholder: 'Search something...',
  scrollRef: null,
  showSearch: true,
  loading: false,
  activeTab: 'product',
  onChangeTab: () => {},
  data: [],
  renderItem: ({ data, index }) => {},
  style: {},
  hasFilter: false,
  headerSticky: false,
  headerComponent: false,
  withLoadMore: false,
  withFetch: false,
  loadingPagination: false,
  skip: 0,
  limit: 10,
  totalData: 0,
  onSearch: () => {},
  onLoadMore: () => {},
  mainBanner: false,
};

const ListStickyHeader = (props) => {
  const dispatch = useDispatch();
  const insets = useSafeAreaInsets();
  const height = fullHeight - insets.top - 55;
  const [isSticky, setIsSticky] = useState(false);
  const {
    name = '',
    showFilterMobile = false,
    onExportPress = null,
    scrollRef = null,
    showSearch = true,
    loading = false,
    loadingPagination = false,
    activeTab = 'product',
    onChangeTab = () => {},
    data = [],
    renderItem = ({ data, index }) => {},
    style = {},
    hasFilter = false,
    headerSticky = false,
    headerComponent = false,
    withLoadMore = false,
    withFetch = false,
    skip = 0,
    limit = 10,
    totalData = 0,
    onSearch = () => {},
    onLoadMore = () => {},
    mainBanner,
  } = props;

  const calculatePage = Math.round(totalData / limit);
  const pageCount = calculatePage ? calculatePage : 1;
  const calculatePageNumber = skip / limit + 1;
  const pageNumber = calculatePageNumber ? calculatePageNumber : 1;

  const isCloseToBottom = ({
    layoutMeasurement,
    contentOffset,
    contentSize,
  }) => {
    const paddingToBottom = 50;
    return (
      layoutMeasurement.height + contentOffset.y >=
      contentSize.height - paddingToBottom
    );
  };

  const onLoadMoreHandler = () => {
    if (!loadingPagination) {
      if (pageCount >= pageNumber && totalData > limit && skip <= totalData) {
        const newSkip = skip + limit;
        try {
          if (withFetch) {
            dispatch(paginationAdded({ skip: newSkip, limit: limit }));
          }
          onLoadMore(newSkip);
        } catch (error) {}
      }
    }
  };

  return (
    <ScrollView
      ref={(val) => (scrollRef ? scrollRef(val) : null)}
      {...props}
      showsVerticalScrollIndicator={false}
      style={[{ height }, style]}
      stickyHeaderIndices={isSticky ? [1] : []}
      scrollEventThrottle={201}
      onScroll={({ nativeEvent }) => {
        if (nativeEvent?.contentOffset?.y >= 201) {
          if (!isSticky) {
            setIsSticky(true);
          }
        } else {
          if (isSticky) {
            setIsSticky(false);
          }
        }
        if (withLoadMore && isCloseToBottom(nativeEvent)) {
          onLoadMoreHandler();
        }
      }}>
      <View>{mainBanner ? mainBanner : false}</View>
      <View style={{ zIndex: 1001, elevation: 1001 }}>
        <CatalogTab
          activeTab={activeTab}
          onCallback={(value) => onChangeTab(value)}
        />
        {showSearch ? (
          <View style={{ width: '100%', flexDirection: 'row' }}>
            <View
              style={{
                width:
                  fullWidth -
                  (showFilterMobile ? 55 : 0) -
                  (onExportPress ? 55 : 0),
              }}>
              <SearchInputMobile
                searchKey={'name'}
                noBorderBottom={showFilterMobile || Boolean(onExportPress)}
                placeholder={
                  props.placeholder ? props.placeholder : 'Search something...'
                }
                onSearch={async (value) => {
                  onSearch(value);
                }}
              />
            </View>
            {showFilterMobile ? (
              <View style={styles.filterIconContainer}>
                <TouchableOpacity
                  activeOpacity={0.8}
                  style={styles.filterIconWrapper}
                  onPress={() => {
                    dispatch(showPopup({ name: 'showFilter', value: name }));
                  }}>
                  <Image
                    style={{ width: 20, height: 20 }}
                    width={20}
                    height={20}
                    svg={true}
                    source={require('@assets/img/icons/filter-grey.svg')}
                  />
                  {hasFilter ? (
                    <View style={styles.hasFilter}>
                      <Ionicons
                        name="checkmark"
                        size={11}
                        color={Colors.white}
                      />
                    </View>
                  ) : (
                    false
                  )}
                </TouchableOpacity>
              </View>
            ) : (
              false
            )}
            {onExportPress ? (
              <View style={styles.filterIconContainer}>
                <TouchableOpacity
                  activeOpacity={0.8}
                  style={styles.filterIconWrapper}
                  onPress={onExportPress}>
                  <Ionicons
                    name="ellipsis-vertical"
                    size={20}
                    color={Colors.black5}
                  />
                </TouchableOpacity>
              </View>
            ) : (
              false
            )}
          </View>
        ) : (
          false
        )}
        {headerSticky ? headerSticky : false}
      </View>
      {headerComponent ? headerComponent : false}
      {loading ? (
        <Loader />
      ) : data?.length ? (
        data.map((item, idx) => renderItem({ data: item, index: idx }))
      ) : (
        <View style={styles.noData}>
          <SociollaText style={styles.noDataText}>No data found</SociollaText>
        </View>
      )}
      {loadingPagination ? <Loader /> : false}
      <View style={{ height: insets.bottom + 20 }} />
    </ScrollView>
  );
};

ListStickyHeader.propTypes = propTypes;
ListStickyHeader.defaultProps = defaultProps;

const styles = StyleSheet.create({
  noData: {
    width: '100%',
    paddingHorizontal: 15,
    paddingVertical: 25,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
  },
  noDataText: { fontSize: 13 },
  filterIconContainer: {
    width: 55,
    backgroundColor: Colors.white,
  },
  filterIconWrapper: {
    width: 45,
    height: 45,
    marginVertical: 10,
    backgroundColor: Colors.grey3,
    borderRadius: 4,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  hasFilter: {
    position: 'absolute',
    bottom: 10,
    right: 10,
    width: 13,
    height: 13,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: Colors.blue0,
    borderRadius: 15,
  },
});

export default React.memo(ListStickyHeader, (prev, next) => prev == next);
